import React from 'react';
import { Box, Chip, Link, Stack, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PendingIcon from '@mui/icons-material/Pending';
import { Link as RouterLink } from 'react-router-dom';
import { useSaleOrderId } from '../../../context/SaleOrderScopeContext';

import { TypeServiceEnum } from '../../../../../../features/salesOrder/types';
import {
	PROVIDER_PAYABLE_LINE_STATUS_COLOR,
	PROVIDER_PAYABLE_LINE_STATUS_LABEL,
	PROVIDER_PAYABLE_LINE_TYPE_LABEL,
} from '../../../../../../features/salesOrder/constants';
import { generatePaymentsUrl } from '../../../helpers';
import { useProviderPayables } from '../../../context/ProviderPayablesContext';
import CopyableId from '../../CopyableId';

interface ServicePurchaseOrderDetailProps {
	serviceId: string;
	serviceType: TypeServiceEnum;
}

const isTicketBasedService = (serviceType: TypeServiceEnum): boolean =>
	serviceType === TypeServiceEnum.Flight || serviceType === TypeServiceEnum.FlightAncillary;

const getEmptyMessage = (serviceType: TypeServiceEnum): string => {
	if (isTicketBasedService(serviceType)) {
		return 'Aún no hay orden de compra disponible para este servicio. Se genera automáticamente al ingresar los números de ticket.';
	}

	return 'Aún no hay orden de compra disponible para este servicio. Se genera automáticamente al guardar el servicio.';
};

const formatUsd = (amount: number): string =>
	`USD ${amount.toLocaleString('es-UY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const ServicePurchaseOrderDetail: React.FC<ServicePurchaseOrderDetailProps> = ({
	serviceId,
	serviceType,
}: ServicePurchaseOrderDetailProps) => {
	const saleOrderId = useSaleOrderId();
	const { getLinesForService, loading } = useProviderPayables();
	const lines = getLinesForService(serviceId);

	const renderContent = () => {
		if (loading && lines.length === 0) {
			return (
				<Box
					sx={{
						borderRadius: 1,
						border: (theme) => `1px solid ${theme.palette.divider}`,
						bgcolor: 'background.paper',
						p: 2,
						mt: 0.5,
					}}
				>
					<Typography variant="body2" color="text.secondary">
						Cargando órdenes de compra…
					</Typography>
				</Box>
			);
		}

		if (lines.length === 0) {
			return (
				<Box
					sx={{
						borderRadius: 1,
						border: (theme) => `1px solid ${theme.palette.divider}`,
						bgcolor: 'background.paper',
						p: 2,
						mt: 0.5,
					}}
				>
					<Stack direction="row" alignItems="center" spacing={1} mb={1}>
						<Box sx={{ color: '#888', display: 'flex', alignItems: 'center' }}>
							<PendingIcon fontSize="small" />
						</Box>
						<Typography variant="subtitle2" sx={{ color: '#888', fontWeight: 600 }}>
							Sin orden de compra
						</Typography>
					</Stack>
					<Typography variant="body2" color="text.secondary">
						{getEmptyMessage(serviceType)}
					</Typography>
				</Box>
			);
		}

		return (
			<Stack spacing={1.5} mt={0.5}>
				{lines.map((line) => {
					const remaining = line.pending ?? line.payableAmount - line.totalPaid;
					const reference = line.ticket || line.providerName || `#${line.id.substring(0, 8)}`;

					return (
						<Box
							key={line.id}
							sx={{
								borderRadius: 1,
								border: (theme) => `1px solid ${theme.palette.divider}`,
								bgcolor: 'background.paper',
								p: 2,
							}}
						>
							<Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={1} mb={1}>
								<Box>
									<Typography variant="body2" sx={{ fontWeight: 600 }}>
										{PROVIDER_PAYABLE_LINE_TYPE_LABEL[line.payableType]} · {reference}
									</Typography>
									{line.passengerName && (
										<Typography variant="body2" color="text.secondary">
											{line.passengerName}
										</Typography>
									)}
									{line.providerName && (
										<Typography variant="caption" color="text.secondary">
											{line.providerName}
										</Typography>
									)}
								</Box>
								<Chip
									label={PROVIDER_PAYABLE_LINE_STATUS_LABEL[line.paymentStatus]}
									color={PROVIDER_PAYABLE_LINE_STATUS_COLOR[line.paymentStatus]}
									size="small"
								/>
							</Stack>

							<Stack spacing={0.3}>
								<Typography variant="body2" color="text.secondary">
									<Box component="span" sx={{ fontWeight: 600 }}>
										A pagar:{' '}
									</Box>
									{formatUsd(line.payableAmount)}
								</Typography>
								<Typography variant="body2" color="text.secondary">
									<Box component="span" sx={{ fontWeight: 600 }}>
										Pagado:{' '}
									</Box>
									{formatUsd(line.totalPaid)}
								</Typography>
								<Typography variant="body2" color="text.secondary">
									<Box component="span" sx={{ fontWeight: 600 }}>
										Pendiente:{' '}
									</Box>
									{formatUsd(remaining)}
								</Typography>
							</Stack>

							<Box mt={1}>
								<CopyableId copiableIdValue={line.id} label="ID de orden de compra" />
							</Box>

							<Link
								component={RouterLink}
								to={generatePaymentsUrl(saleOrderId, line.id)}
								variant="body2"
								sx={{ display: 'inline-block', mt: 1 }}
							>
								Ver en pagos a proveedores
							</Link>
						</Box>
					);
				})}
			</Stack>
		);
	};

	return (
		<Box
			mt={2}
			sx={{
				width: '100%',
				maxWidth: '100%',
				boxSizing: 'border-box',
			}}
		>
			<Stack direction="row" alignItems="center" spacing={1} mb={1}>
				<InfoOutlinedIcon fontSize="small" color="action" />
				<Typography variant="h6" color="Grey">
					Información de orden de compra
				</Typography>
			</Stack>

			{renderContent()}
		</Box>
	);
};

export default ServicePurchaseOrderDetail;
